import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { useAuthContext } from "./AuthContext";
import { getSocketWorkspace } from "../utils/socketWorkspace";
import { SOCKET_SERVER_URL } from "../constants/socketServer";
import { buildTeamPresenceXpro4Payload } from "../utils/teamPresenceTelemetry";
import { clearMainAppPresenceSocketMergeCache, rememberLocalPresenceEmit } from "../utils/teamPresenceUsersSocket";
import { postPresenceClientEnv } from "../api/client";
import { hasActiveTenantWorkspaceContext } from "../utils/partnerPortal";
import { resolvePresenceUserId } from "../utils/presenceIdentityFromAuth";

const SOCKET_URL = SOCKET_SERVER_URL;
const SocketContext = createContext();

export const useSocketContext = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const { bearerToken, userData } = useAuthContext();
  const [socketInstance, setSocketInstance] = useState(null);

  useEffect(() => {
    if (bearerToken && userData) {
      if (!socketRef.current) {
        const socket = io(SOCKET_URL, {
          transports: ["websocket"],
          auth: { token: bearerToken },
        });

        socket.on("connect", () => {
          const workspace = getSocketWorkspace();
          const presenceId = resolvePresenceUserId(bearerToken, userData);
          if (presenceId == null) return;
          const base = {
            id: presenceId,
            name: userData.nome,
            workspace,
          };
          const payload = buildTeamPresenceXpro4Payload(base);
          rememberLocalPresenceEmit({ ...payload, socketId: socket.id });
          socket.emit("newUser", payload);
          socket.emit("users", payload);
          // Console plataforma/revenda limpa Company; presence-env é telemetria de tenant.
          if (!hasActiveTenantWorkspaceContext()) return;
          postPresenceClientEnv({
            workspace,
            tabSessionId: payload.tab,
            path: payload.path,
            since: payload.since,
            lastActivityAt: payload.lastActivityAt,
            focused: payload.focused,
            deviceType: payload.deviceType,
            browserName: payload.browserName,
            appVersion: payload.appVersion,
            appTheme: payload.appTheme,
          }).catch(() => {});
        });

        socket.on("connect_error", (error) => {
          console.error("Erro de conexão ao WebSocket:", error);
        });

        socket.on("disconnect", (reason) => {
          // console.log("Desconectado do WebSocket:", reason);
        });

        socketRef.current = socket;
        setSocketInstance(socket);
      }
    }

    return () => {
      if (socketRef.current) {
        clearMainAppPresenceSocketMergeCache();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocketInstance(null);
      }
    };
  }, [bearerToken, userData]);

  const emit = (event, data) => {
    socketRef.current?.emit(event, data);
  };

  const on = (event, callback) => {
    socketRef.current?.on(event, callback);
  };

  const off = (event, callback) => {
    socketRef.current?.off(event, callback);
  };

  return (
    <SocketContext.Provider value={{ socket: socketInstance, emit, on, off }}>
      {children}
    </SocketContext.Provider>
  );
};
